import { Typography } from "common/components/Typography";
import { formatPrice, getEndingByCountLT1 } from "common/helpers/text.helpers";
import {
  getComparableAddress,
  getComparableImages,
  isCompFullArea,
  isCompPropertyMatchByUniqueNr,
} from "common/helpers/comparables.helpers";
import { Adjustments, Comparable, MyProperty } from "common/types/common.types";
import React, { useContext, useState } from "react";
import colors from "common/styles/colors";
import { getListString } from "common/helpers/common.helpers";

import { IconButton } from "common/components/buttons/IconButton";
import AddButtonIcon from "../../assets/icons/addButton.svg";
import RemoveButtonIcon from "../../assets/icons/removeButton.svg";
import { PropertyPlaceholderImage } from "../imageGallery/PropertyPlaceholderImage";
import ArrowAdjustmentDownIcon from "../../assets/icons/arrowAdjustmentDown.svg";
import ArrowAdjustmentUpIcon from "../../assets/icons/arrowAdjustmentUp.svg";
import { addAlpha } from "common/helpers/colors.helpers";
import { RelatedRETag } from "../RelatedRETag";
import {
  calcSoldPrice,
  getPercentage,
  getReservationText,
  getYearText,
  isComparableFull,
  uniqueByType,
} from "../../helpers/comparable.helpers";
import { ComparableCardMenu } from "./ComparableCardMenu";
import { MessageType, StatusMessage } from "../../types/api.types";
import {
  getMessageTypeDescription,
  getMessageTypeIcon,
} from "../messages/helpers";
import {
  Box,
  CircularProgress,
  Tooltip,
  tooltipClasses,
  useMediaQuery,
} from "@mui/material";
import { TransactionsRCContext } from "../TransactionsRCProvider";
import { ComparableNumber } from "./ComparableNumber";
import { getPropertyListString } from "../../helpers/property.helpers";
import { PropertyImageGallery } from "../imageGallery/PropertyImageGallery";
import { LinkButton } from "../buttons/LinkButton";
import LightTooltip from "../tooltips/StyledTooltip";
import { Link, useNavigate } from "react-router-dom";
import { theme } from "../../styles/MUITheme";
import { PercentageIndicator } from "../indicators/PercentageIndicator";
import { useTranslation } from "react-i18next";

interface ComparableCardProps {
  comparable: Comparable;
  onClick?: () => void;
  onComparableRemove?: () => void;
  onComparableAdd?: () => void;
  isInCart?: boolean;
  isCorrected?: boolean;
  adjustments?: Adjustments;
  children?: React.ReactNode;
  withButtons?: boolean;
  status?: StatusMessage;
  withNumber?: number;
  isOverview?: boolean;

  noBorder?: boolean;

  property: MyProperty;
}

export const ComparableCard: React.FC<ComparableCardProps> = ({
  comparable,
  onClick,
  onComparableAdd,
  onComparableRemove,
  isCorrected,
  isInCart,
  adjustments,
  children,
  withButtons,
  status,
  withNumber,
  isOverview,

  noBorder,

  property,
}) => {
  const { t, i18n } = useTranslation();
  const images = getComparableImages(comparable.comparable_transaction);

  const address = getComparableAddress(comparable.comparable_transaction);

  const adjustedPriceArea = adjustments?.adjusted_price_area ?? 0;

  const isFull = isComparableFull(comparable);

  const transaction_price =
    isFull && formatPrice(comparable.comparable_transaction.sold_price);
  const transaction_price_area =
    isFull &&
    `(${formatPrice(comparable.comparable_transaction.sold_price_area)}/m²)`;

  const perc =
    isFull &&
    !!adjustments &&
    getPercentage(
      adjustedPriceArea,
      calcSoldPrice(adjustments, comparable.comparable_transaction) /
        comparable.comparable_transaction.area
    );

  const transaction_date = comparable.comparable_transaction.sold_date;

  const captionString = getPropertyListString({
    area: comparable.comparable_transaction.area,
    areaTotal: !isCompFullArea(comparable.comparable_transaction)
      ? comparable.comparable_transaction.area_total
      : undefined,
    rooms: isFull ? comparable.comparable_transaction.rooms : undefined,
    floorNr: isFull ? comparable.comparable_transaction.floor : undefined,
    floorCount: isFull
      ? comparable.comparable_transaction.floor_total
      : undefined,
    year: comparable.comparable_transaction.construction_year,
    renovationYear: isFull
      ? comparable.comparable_transaction.reconstruction_year
      : undefined,
    energyClass: isFull
      ? comparable.comparable_transaction.energy_class
      : undefined,
    finishing: isFull
      ? !!comparable.comparable_transaction.finishing
      : undefined,
  });

  const percentageColor =
    !!perc && (perc.percentage < 0 ? colors.red : colors.green);

  const matchedProject = comparable.comparable_transaction.matched_project;
  const matchedListing = comparable.comparable_transaction.matched_listing;

  const StatusIcon = !!status ? getMessageTypeIcon(status.type) : undefined;

  const { isBuying } = useContext(TransactionsRCContext);

  const [isImageHovered, setIsImageHovered] = useState(false);

  const navigate = useNavigate();
  const [isNavigating, setIsNavigating] = useState(false); // to prevent visible tooltip on navigation

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const FakedIcon = getMessageTypeIcon(MessageType.FAKED);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        cursor: !!onClick ? "pointer" : "auto",
        position: "relative",
        minHeight: "180px",
        maxHeight: "200px",
        // fixes issue of children height:100% taking only "height = aspect-rated height in relation to image width"
        // https://stackoverflow.com/questions/8468066/child-inside-parent-with-min-height-100-not-inheriting-height
        height: "1px",

        ...(!noBorder && {
          border: `1px solid ${colors.greyborder}`,
          borderRadius: "10px",
          backgroundColor: colors.white,
        }),
      }}
      onClick={onClick}
    >
      <PropertyImageGallery
        key={comparable.comparable_transaction.id}
        images={images}
        borderBottomLeftRadius={noBorder ? 0 : 10}
        borderTopLeftRadius={noBorder ? 0 : 10}
        height="100%"
        width={isMobile ? 110 : 180}
        borderRadius={0}
        onMouseEnter={() => setIsImageHovered(true)}
        onMouseLeave={() => setIsImageHovered(false)}
      />
      {!!isCorrected && (
        <div
          style={{
            position: "absolute",
            top: 4,
            left: 4,
            backgroundColor: colors.grey,
            lineHeight: 0,
            borderRadius: "5px",
            padding: "4px 8px",
          }}
        >
          <Typography
            variant="h4"
            textStyle={{ color: colors.white, fontSize: 12 }}
          >
            {t("comparableCard.reviewed")}
          </Typography>
        </div>
      )}
      {!!images.length && isImageHovered && (
        <div
          style={{
            position: "absolute",
            bottom: 0,
            left: 0,
            backgroundColor: addAlpha(colors.grey, 0.8),
            lineHeight: 0,
            borderBottomLeftRadius: "10px",
            padding: "4px 8px",
            color: colors.white,
            height: "28px",
            display: "flex",
            alignItems: "center",
            fontSize: 12,
            pointerEvents: "none",
          }}
        >
          {i18n.language === "lt"
            ? `${images.length} nuotrauk${getEndingByCountLT1(
                images.length
              ).toLowerCase()}`
            : `${images.length} photo${images.length === 1 ? "" : "s"}`}
        </div>
      )}
      <Box
        sx={{
          position: "relative",
          display: "flex",
          justifyContent: "space-between",
          flexDirection: "column",
          minWidth: { xs: "200px", sm: "280px" },
          width: "100%",
        }}
      >
        {!!onComparableRemove && onComparableAdd && (
          <div style={{ position: "absolute", right: 0, top: 0 }}>
            <IconButton
              onPress={() => {
                if (isInCart) {
                  onComparableRemove();
                } else {
                  onComparableAdd();
                }
              }}
            >
              {isBuying?.includes(comparable.comparable_transaction.id) ? (
                <CircularProgress size={20} />
              ) : isInCart ? (
                <RemoveButtonIcon />
              ) : (
                <AddButtonIcon />
              )}
            </IconButton>
          </div>
        )}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            flex: 5,
            gap: 2,
            padding: 10,
          }}
        >
          {!!matchedProject && (
            <Typography variant="h5" textStyle={{ color: colors.purple3 }}>
              {matchedProject.project_name}
            </Typography>
          )}
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            {withNumber !== undefined && (
              <ComparableNumber compNumber={withNumber} />
            )}
            {!!property &&
              isCompPropertyMatchByUniqueNr(
                comparable.comparable_transaction,
                property
              ) && (
                <div style={{ marginRight: "5px" }}>
                  <LightTooltip title={t("comparableCard.uniqueNrMatch")}>
                    <div style={{ height: "18px" }}>
                      <FakedIcon />
                    </div>
                  </LightTooltip>
                </div>
              )}
            {!!StatusIcon && !!status && !isNavigating && (
              <div style={{ marginRight: "5px" }}>
                <LightTooltip
                  title={
                    <div>
                      {!!status.message
                        ? status.message
                        : getMessageTypeDescription(status.type)}
                      <LinkButton
                        onClick={() => {
                          setIsNavigating(true);
                          navigate(
                            `${comparable.comparable_transaction.id}/messages`
                          );
                          setTimeout(() => {
                            setIsNavigating(false);
                          }, 200);
                        }}
                      >
                        {t("common.more")}
                      </LinkButton>
                    </div>
                  }
                  arrow
                >
                  <div style={{ height: "18px" }}>
                    <StatusIcon />
                  </div>
                </LightTooltip>
              </div>
            )}
            <Typography variant="h5" textStyle={{ marginRight: "20px" }}>
              {address}
            </Typography>
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            <Typography variant="h5">{transaction_price}</Typography>
            <Typography style={{ fontSize: 14, marginLeft: 6 }}>
              {transaction_price_area}
            </Typography>
            {!!perc && (
              <PercentageIndicator
                originalValue={
                  comparable.comparable_transaction.sold_price_area
                }
                correctedValue={adjustments?.adjusted_price_area ?? 0}
              />
            )}
          </div>
          <Typography textStyle={{ fontSize: 12, color: colors.green }}>
            {t("comparableCard.transactionDate")} {transaction_date}
          </Typography>
          <Typography
            variant="caption"
            textStyle={{ fontSize: 12, marginTop: 5 }}
          >
            {captionString}
          </Typography>
          {!isOverview && !isFull && !!matchedListing && (
            <Tooltip
              title={`Aukštas ${matchedListing.floor} iš ${matchedListing.floor_total}`}
            >
              <Link
                to={`${comparable.comparable_transaction.id}/compare`}
                style={{ marginTop: 5, textDecorationColor: colors.black }}
              >
                <Typography textStyle={{ fontSize: 12 }}>
                  Papildoma informacija iš skelbimo
                </Typography>
              </Link>
            </Tooltip>
          )}
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            padding: "4px 12px",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              gap: "8px",
            }}
          >
            {uniqueByType(
              comparable.comparable_transaction.related_re ?? []
            ).map((related, index) => (
              <RelatedRETag key={index} re={related} />
            ))}
          </div>
          {!isOverview && <ComparableCardMenu comparable={comparable} />}
        </div>
        {children}
      </Box>
    </Box>
  );
};
